import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import classNames from "classnames";

import { Button } from "@components/core";
import getRelayPoints from "@utils/call-api/carriers-call";
import parseGoogleAddressComponents from "@utils/parseGoogleAddressComponents";

import AddressIndicator from "./AddressIndicator";
import AddressesSearchBar from "./AddressesSearchBar";
import RelayPointsMap from "./RelayPointsMap";
import RelayPointsList from "./RelayPointsList";

const RelayPointsListMap = ({
  startingAddress,
  carriersToDisplay,
  withSubmitButtons = false,
  onRelayPointSubmit = () => {},
}) => {
  const t = useTranslations("addresses");

  const [displayMode, setDisplayMode] = useState("list"); // "list" or "map"
  const [geocoder, setGeocoder] = useState();
  const [referencePlace, setReferencePlace] = useState();
  const [selectedRelayPoint, setSelectedRelayPoint] = useState();
  const [relayPointsList, setRelayPointsList] = useState(null);
  const [searchingAddress, setSearchingAddress] = useState(false);

  const [initializing, setInitializing] = useState(true);
  const [loadingRelayPoints, setLoadingRelayPoints] = useState(false);

  function onAddressSelected(place) {
    setSearchingAddress(false);
    setRelayPointsList([]);
    setReferencePlace(place);
    searchRelayPoints(place);
  }

  function setStartingAddressAsReference() {
    if (!geocoder || !startingAddress?.country) return;

    const { line1, zip, city, country } = startingAddress;
    const addressFormatted = `${line1}, ${zip} ${city}, ${country}`;

    geocoder.geocode({ address: addressFormatted }, (results, status) => {
      if (status === "OK") {
        setReferencePlace(results[0]);
        searchRelayPoints(results[0]);
        setInitializing(false);
      }
    });
  }

  async function searchRelayPoints(place) {
    setLoadingRelayPoints(true);
    const longitude = place.geometry.location.lng().toFixed(6);
    const latitude = place.geometry.location.lat().toFixed(6);
    const addressParsed = parseGoogleAddressComponents(place.address_components);
    const address = {
      line1: `${addressParsed.street_number} ${addressParsed.street}`.trim(),
      zip: addressParsed.postal_code,
      city: addressParsed.city,
      country: addressParsed.country,
    };

    try {
      const responses = await getRelayPoints({
        carriers_to_request: carriersToDisplay,
        address,
        latitude,
        longitude,
        limit_results: 10,
      });
      const relayPointsRes = responses.flat();

      setLoadingRelayPoints(false);
      setRelayPointsList(relayPointsRes);
    } catch (err) {
      if (err.name !== "CanceledError") throw err;
    }
  }

  function submitRelayPointSelection() {
    if (selectedRelayPoint) onRelayPointSubmit(selectedRelayPoint);
  }

  useEffect(() => {
    if (geocoder) setStartingAddressAsReference();
  }, [geocoder, carriersToDisplay]);

  return (
    <div
      className="pb-2 pt-3 d-flex flex-column"
      data-testid="carriers-list-map"
    >
      <div className="d-flex flex-column mb-4">
        <div className="mb-3">
          <AddressesSearchBar
            placeholder={t("search_address")}
            onAddressSelected={onAddressSelected}
            limitToCountry={startingAddress?.country}
            hidden={referencePlace && !searchingAddress}
          />

          {referencePlace && !searchingAddress && (
            <AddressIndicator
              place={referencePlace}
              onChangeButtonClick={() => setSearchingAddress(true)}
            />
          )}
        </div>

        {(relayPointsList || loadingRelayPoints) && (
          <div className="d-flex gap-1">
            <Button
              variant={displayMode === "list" ? "outline-primary" : "ghost-primary"}
              fullWidth
              onClick={() => setDisplayMode("list")}
              className={classNames("short-padding", displayMode !== "list" && "bg-white")}
            >
              {t("show_carrier_list")}
            </Button>

            <Button
              variant={displayMode === "map" ? "outline-primary" : "ghost-primary"}
              fullWidth
              onClick={() => setDisplayMode("map")}
              className={classNames("short-padding", displayMode !== "map" && "bg-white")}
            >
              {t("show_carrier_map")}
            </Button>
          </div>
        )}
      </div>

      <RelayPointsList
        relayPointsList={relayPointsList}
        selectedRelayPoint={selectedRelayPoint}
        setSelectedRelayPoint={setSelectedRelayPoint}
        loadingRelayPoints={loadingRelayPoints}
        initializing={initializing}
        hidden={displayMode === "map"}
        submitButton={withSubmitButtons}
        onSubmit={submitRelayPointSelection}
      />

      <RelayPointsMap
        referencePlace={referencePlace}
        relayPointsList={relayPointsList}
        selectedRelayPoint={selectedRelayPoint}
        setSelectedRelayPoint={setSelectedRelayPoint}
        geocoder={geocoder}
        setGeocoder={setGeocoder}
        hidden={displayMode === "list"}
        submitButton={withSubmitButtons}
        onSubmit={submitRelayPointSelection}
      />
    </div>
  );
};

export default RelayPointsListMap;
